import { Accessor, createSignal } from "solid-js";

import {
  PRESETS,
  PRESET_IDS,
  PresetId,
  TYPUS_COLOURS,
} from "@revolt/ui/themes/engine/presets";
import {
  FONT_KEYS,
  Fonts,
  MONOSPACE_FONT_KEYS,
  MonospaceFonts,
} from "@revolt/ui/themes/fonts";

import { State } from "..";

import { AbstractStore } from ".";

export type TypeTheme = {
  /**
   * Base theme preset
   */
  preset: "you";

  /**
   * TYPUS preset the look starts from
   */
  presetId: PresetId;

  /**
   * Corner roundness relative to the preset (50-150 %)
   */
  radiusScale: number;

  /**
   * Whether servers may show their own theme while being viewed
   */
  useServerThemes: boolean;

  /**
   * Animation level ("system" follows prefers-reduced-motion)
   */
  motion: "system" | "full" | "reduced" | "off";

  /**
   * Light/dark mode
   */
  mode: "light" | "dark" | "system";

  /**
   * Accent
   * (Material You)
   */
  m3Accent: string;

  /**
   * Constrast
   * (Material You)
   */
  m3Contrast: number;

  /**
   * Variant
   * (Material You)
   */
  m3Variant:
    | "monochrome"
    | "neutral"
    | "tonal_spot"
    | "vibrant"
    | "expressive"
    | "fidelity"
    | "content"
    | "rainbow"
    | "fruit_salad";

  /**
   * Whether to permit blurry surfaces
   */
  blur: boolean;

  /**
   * Interface font
   */
  interfaceFont: Fonts;

  /**
   * Monospace font
   */
  monospaceFont: MonospaceFonts;

  /**
   * Message size
   */
  messageSize: number;

  /**
   * Spacing between message groups
   */
  messageGroupSpacing: number;

  /**
   * How much to darken the app wallpaper (0-80 %)
   */
  wallpaperDim: number;

  /**
   * Blur behind panels when a wallpaper is set (0-20 px)
   */
  wallpaperBlur: number;

  /**
   * Opacity of the main panels when a wallpaper is set (40-100 %)
   */
  panelOpacity: number;

  /**
   * Wallpaper stored on the server (Autumn "backgrounds" file id),
   * synced across devices
   */
  wallpaperFileId?: string;
};

export type SelectedTheme = Pick<
  TypeTheme,
  | "blur"
  | "interfaceFont"
  | "monospaceFont"
  | "messageSize"
  | "messageGroupSpacing"
  | "wallpaperDim"
  | "wallpaperBlur"
  | "panelOpacity"
  | "presetId"
  | "radiusScale"
> & {
  preset: "you";
  darkMode: boolean;

  /**
   * Effective animation level after applying the system preference
   */
  motionLevel: "full" | "reduced" | "off";

  accent: string;
  contrast: number;
  variant: TypeTheme["m3Variant"];
};

/**
 * Manages theme information
 */
export class Theme extends AbstractStore<"theme", TypeTheme> {
  prefersDark: Accessor<boolean>;
  prefersReducedMotion: Accessor<boolean>;

  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "theme");

    // handle prefers-color-scheme value and changes
    const [prefersDark, setPrefersDark] = createSignal(
      window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches,
    );

    this.prefersDark = prefersDark;

    // handle prefers-reduced-motion value and changes
    const reducedMotionQuery = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    const [prefersReducedMotion, setPrefersReducedMotion] = createSignal(
      !!reducedMotionQuery?.matches,
    );
    this.prefersReducedMotion = prefersReducedMotion;
    reducedMotionQuery?.addEventListener("change", (event) =>
      setPrefersReducedMotion(event.matches),
    );

    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (event) => setPrefersDark(event.matches));

    this.toggleBlur = this.toggleBlur.bind(this);
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeTheme {
    return {
      preset: "you",
      presetId: "oceano",
      radiusScale: 100,
      motion: "system",
      useServerThemes: true,
      mode: "system",

      // TYPUS "Oceano" defaults
      m3Accent: TYPUS_COLOURS.body,
      m3Contrast: 0.0,
      m3Variant: "fidelity",

      interfaceFont: "Space Mono",
      monospaceFont: "Space Mono",

      blur: true,
      messageSize: 14,
      messageGroupSpacing: 12,

      wallpaperDim: 30,
      wallpaperBlur: 12,
      panelOpacity: 75,
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeTheme>): TypeTheme {
    const data: TypeTheme = this.default();

    if (["light", "dark", "system"].includes(input.mode!)) {
      data.mode = input.mode!;
    }

    if (["you", "neutral"].includes(input.preset!)) {
      data.preset = input.preset!;
    }

    if (typeof input.m3Contrast === "number") {
      data.m3Contrast = input.m3Contrast;
    }

    if (
      input.m3Accent &&
      input.m3Accent.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})/)
    ) {
      data.m3Accent = input.m3Accent;
    }

    if (
      [
        "monochrome",
        "neutral",
        "tonal_spot",
        "vibrant",
        "expressive",
        "fidelity",
        "content",
        "rainbow",
        "fruit_salad",
      ].includes(input.m3Variant!)
    ) {
      data.m3Variant = input.m3Variant!;
    }

    if (typeof input.blur === "boolean") {
      data.blur = input.blur;
    }

    if (typeof input.messageSize === "number") {
      data.messageSize = input.messageSize;
    }

    if (typeof input.messageGroupSpacing === "number") {
      data.messageGroupSpacing = input.messageGroupSpacing;
    }

    const clamp = (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value));

    if (PRESET_IDS.includes(input.presetId!)) {
      data.presetId = input.presetId!;
    }

    if (typeof input.useServerThemes === "boolean") {
      data.useServerThemes = input.useServerThemes;
    }

    if (["system", "full", "reduced", "off"].includes(input.motion!)) {
      data.motion = input.motion!;
    }

    if (typeof input.radiusScale === "number") {
      data.radiusScale = clamp(input.radiusScale, 50, 150);
    }

    if (typeof input.wallpaperDim === "number") {
      data.wallpaperDim = clamp(input.wallpaperDim, 0, 80);
    }

    if (typeof input.wallpaperBlur === "number") {
      data.wallpaperBlur = clamp(input.wallpaperBlur, 0, 20);
    }

    if (
      typeof input.wallpaperFileId === "string" &&
      /^[\w-]{1,128}$/.test(input.wallpaperFileId)
    ) {
      data.wallpaperFileId = input.wallpaperFileId;
    }

    if (typeof input.panelOpacity === "number") {
      data.panelOpacity = clamp(input.panelOpacity, 40, 100);
    }

    if (
      typeof input.monospaceFont === "string" &&
      MONOSPACE_FONT_KEYS.includes(input.monospaceFont)
    ) {
      data.monospaceFont = input.monospaceFont;
    }

    if (
      typeof input.interfaceFont === "string" &&
      FONT_KEYS.includes(input.interfaceFont)
    ) {
      data.interfaceFont = input.interfaceFont;
    }

    return data;
  }

  /**
   * Get the currently selected theme (considering system settings)
   */
  get activeTheme(): SelectedTheme {
    const opts = this.get();

    switch (opts.preset) {
      case "you":
        return {
          blur: opts.blur,
          interfaceFont: opts.interfaceFont,
          monospaceFont: opts.monospaceFont,
          messageSize: opts.messageSize,
          messageGroupSpacing: opts.messageGroupSpacing,
          wallpaperDim: opts.wallpaperDim,
          wallpaperBlur: opts.wallpaperBlur,
          panelOpacity: opts.panelOpacity,
          presetId: opts.presetId,
          radiusScale: opts.radiusScale,
          motionLevel:
            opts.motion === "system"
              ? this.prefersReducedMotion()
                ? "reduced"
                : "full"
              : opts.motion,
          preset: "you",
          darkMode:
            opts.mode === "dark" ||
            (opts.mode === "system" && this.prefersDark()),

          accent: opts.m3Accent,
          contrast: opts.m3Contrast,
          variant: opts.m3Variant,
        };
    }
  }

  /**
   * Get light/dark/system mode
   */
  get mode() {
    return this.get().mode;
  }

  /**
   * Set light/dark/system mode
   * @param mode Mode
   */
  setMode(mode: TypeTheme["mode"]) {
    this.set("mode", mode);
  }

  /**
   * Get current preset
   */
  get preset() {
    return this.get().preset;
  }

  /**
   * Set the active preset
   * @param preset Preset
   */
  setPreset(preset: TypeTheme["preset"]) {
    this.set("preset", preset);
  }

  /**
   * Get current accent
   */
  get m3Accent() {
    return this.get().m3Accent;
  }

  /**
   * Set the accent of the Material You theme
   * @param accent Accent
   */
  setM3Accent(accent: string) {
    this.set("m3Accent", accent);
  }

  /**
   * Get current contrast
   */
  get m3Contrast() {
    return this.get().m3Contrast;
  }

  /**
   * Set the contrast of the Material You theme
   * @param contrast Contrast
   */
  setM3Contrast(contrast: number) {
    this.set("m3Contrast", contrast);
  }

  /**
   * Get current variant
   */
  get m3Variant() {
    return this.get().m3Variant;
  }

  /**
   * Set the variant of the Material You theme
   * @param variant Variant
   */
  setM3Variant(variant: TypeTheme["m3Variant"]) {
    this.set("m3Variant", variant);
  }

  /**
   * Get current blur state
   */
  get blur() {
    return this.get().blur;
  }

  /**
   * Toggle blur state
   */
  toggleBlur() {
    this.set("blur", !this.blur);
  }

  /**
   * Get current interface font
   */
  get interfaceFont() {
    return this.get().interfaceFont;
  }

  /**
   * Set interface font
   */
  setInterfaceFont(font: Fonts) {
    return this.set("interfaceFont", font);
  }

  /**
   * Get current monospace font
   */
  get monospaceFont() {
    return this.get().monospaceFont;
  }

  /**
   * Set monospace font
   */
  setMonospaceFont(font: MonospaceFonts) {
    return this.set("monospaceFont", font);
  }

  /**
   * Get current message size
   */
  get messageSize() {
    return this.get().messageSize;
  }

  /**
   * Set message size
   */
  set messageSize(size: number) {
    this.set("messageSize", size);
  }

  /**
   * Get current message group spacing
   */
  get messageGroupSpacing() {
    return this.get().messageGroupSpacing;
  }

  /**
   * Set message group spacing
   */
  set messageGroupSpacing(space: number) {
    this.set("messageGroupSpacing", space);
  }

  /**
   * Get the active TYPUS preset
   */
  get presetId() {
    return this.get().presetId;
  }

  /**
   * Switch to a TYPUS preset, resetting colours to the preset's defaults
   * @param id Preset id
   */
  applyPreset(id: PresetId) {
    const preset = PRESETS[id];
    this.set("presetId", id);
    this.set("m3Accent", preset.defaults.source);
    this.set("m3Variant", preset.defaults.variant);
    if (preset.defaults.mode) this.set("mode", preset.defaults.mode);
  }

  /**
   * Whether server themes are applied
   */
  get useServerThemes() {
    return this.get().useServerThemes;
  }

  /**
   * Toggle server themes
   */
  set useServerThemes(value: boolean) {
    this.set("useServerThemes", value);
  }

  /**
   * Get animation level
   */
  get motion() {
    return this.get().motion;
  }

  /**
   * Set animation level
   */
  set motion(value: TypeTheme["motion"]) {
    this.set("motion", value);
  }

  /**
   * Get corner roundness (%)
   */
  get radiusScale() {
    return this.get().radiusScale;
  }

  /**
   * Set corner roundness (%)
   */
  set radiusScale(value: number) {
    this.set("radiusScale", value);
  }

  /**
   * Get wallpaper darkening (%)
   */
  get wallpaperDim() {
    return this.get().wallpaperDim;
  }

  /**
   * Set wallpaper darkening (%)
   */
  set wallpaperDim(value: number) {
    this.set("wallpaperDim", value);
  }

  /**
   * Get blur behind panels (px)
   */
  get wallpaperBlur() {
    return this.get().wallpaperBlur;
  }

  /**
   * Set blur behind panels (px)
   */
  set wallpaperBlur(value: number) {
    this.set("wallpaperBlur", value);
  }

  /**
   * Get the server-side wallpaper file id
   */
  get wallpaperFileId() {
    return this.get().wallpaperFileId;
  }

  /**
   * Set the server-side wallpaper file id
   */
  set wallpaperFileId(id: string | undefined) {
    this.set("wallpaperFileId", id);
  }

  /**
   * Get panel opacity (%)
   */
  get panelOpacity() {
    return this.get().panelOpacity;
  }

  /**
   * Set panel opacity (%)
   */
  set panelOpacity(value: number) {
    this.set("panelOpacity", value);
  }
}
